import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { NewReservationDialog } from "@/components/NewReservationDialog";
import {
  CalendarDays, LayoutList, Users, Inbox, BarChart3, Settings, LogOut, Plus, Menu, X, UtensilsCrossed, ArrowLeft,
} from "lucide-react";

const NAV = [
  { to: "/app", label: "Agenda", icon: LayoutList, end: true, testid: "nav-agenda" },
  { to: "/app/calendario", label: "Calendario", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/app/lead", label: "Lead Portale", icon: Inbox, testid: "nav-leads" },
  { to: "/app/clienti", label: "Clienti", icon: Users, testid: "nav-customers" },
  { to: "/app/statistiche", label: "Statistiche", icon: BarChart3, testid: "nav-analytics" },
  { to: "/app/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-settings" },
];

export function Layout({ children }) {
  const { user, business, logout, impersonating, stopImpersonation } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  const doLogout = () => { logout(); nav("/login"); };
  const backToAdmin = async () => { await stopImpersonation(); nav("/admin"); };

  const SideLinks = () => (
    <nav className="flex flex-col gap-1">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          data-testid={n.testid}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-primary/10 hover:text-primary"
            }`
          }
        >
          <n.icon className="h-5 w-5" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Banner impersonazione (superadmin "entrato come" un'attività) */}
      {impersonating && (
        <div className="sticky top-0 z-50 bg-slate-900 text-white px-4 py-2 flex items-center justify-between gap-3 text-sm">
          <span className="truncate">👁️ Stai gestendo <strong>{impersonating}</strong> come amministratore</span>
          <button onClick={backToAdmin} data-testid="stop-impersonation" className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-white/15 hover:bg-white/25 px-3 py-1 font-medium">
            <ArrowLeft className="h-4 w-4" /> Torna all'amministrazione
          </button>
        </div>
      )}
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button className="lg:hidden p-2 -ml-2 text-slate-700" onClick={() => setOpen(true)} data-testid="nav-open-menu">
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-white">
              <UtensilsCrossed className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <p className="font-heading font-bold text-slate-900 text-sm sm:text-base">{business?.name || "La tua attività"}</p>
              <p className="text-[11px] text-primary font-medium">Powered by turismo.latina.it</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="header-new-reservation-btn" onClick={() => setNewOpen(true)} className="rounded-xl">
            <Plus className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Nuova</span> Prenotazione
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-64 shrink-0 flex-col justify-between border-r border-slate-200 bg-white/60 p-4 min-h-[calc(100vh-61px)] sticky top-[61px]">
          <SideLinks />
          <div className="space-y-2">
            <div className="rounded-xl bg-muted p-3 text-xs text-slate-600">
              <p className="font-semibold text-slate-800">{user?.name}</p>
              <p className="truncate">{user?.email}</p>
            </div>
            <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={doLogout} data-testid="nav-logout">
              <LogOut className="h-4 w-4 mr-2" /> Esci
            </Button>
          </div>
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-72 bg-white p-4 flex flex-col justify-between shadow-xl">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-heading font-bold">Menu</span>
                  <button onClick={() => setOpen(false)} className="p-1"><X className="h-5 w-5" /></button>
                </div>
                <SideLinks />
              </div>
              <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={doLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Esci
              </Button>
            </div>
          </div>
        )}

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-6xl w-full mx-auto">{children}</main>
      </div>

      <NewReservationDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}
